import { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import IResponse from './IResponse';

export class ApiMessageBuilder
{
    private readonly labels: IApiErrorLabels;
    private readonly getApiMessage: (key: string) => string;
    private readonly locale: string;

    constructor(labels: IApiErrorLabels, getApiMessage: (key: string) => string, locale: string)
    {
        this.labels = labels;
        this.getApiMessage = getApiMessage;
        this.locale = locale;
    }

    public getApiError(error: (AxiosError<any> | null)): IApiError
    {
        const labels = this.labels;
        const axiosData = error?.response?.data;
        const dataAsString = typeof (axiosData) == 'string' ? axiosData : undefined;
        const status = error?.response?.status ?? 0;

        const data: IResponse | undefined = !axiosData ? undefined : {
            responseId: axiosData.responseId || axiosData.ResponseId || undefined,
            title: axiosData.title || axiosData.Title || undefined,
            message: axiosData.message || axiosData.Message || undefined,
            detail: axiosData.detail || axiosData.Detail || undefined,

            warnings: (axiosData.warnings || axiosData.Warnings)?.map((i: any) => ({
                responseId: i.responseId || i.ResponseId || undefined,
                title: i.title || i.Title || undefined,
                message: i.message || i.Message || undefined,
                details: i.details || i.Details || undefined,
            })) || undefined,

            errors: (axiosData.errors || axiosData.Errors)?.map((i: any) => ({
                detail: i.detail || i.Detail || undefined,
            })) || undefined,

            appInstanceId: axiosData.appInstanceId || axiosData.AppInstanceId || undefined,
            timestamp: axiosData.timestamp || axiosData.timeStamp || axiosData.Timestamp || axiosData.TimeStamp || undefined,
        };

        const instance = data?.appInstanceId ?? '';
        const time = (data?.timestamp ? this.formatDateTime(data.timestamp) : '');
        const errorDetails = data?.detail ?? '';

        if (error?.message == "Network Error")
        {
            return { errorTitle: labels.networkFailure, errorDetails: errorDetails, messages: [labels.pleaseTryAgain], status, instance, time };
        }
        else if (error?.response?.status === 401)
        {
            // the global interceptor redirects to the login page when a 401 is encountered
            // an empty message array prevents the popup from briefly showing
            return { errorTitle: '', errorDetails: '', messages: [], status, instance, time };
        }

        const errorTitle = this.getTitle(data?.responseId, data?.title);

        if (data?.errors && data.errors.length > 0)
        {
            const messages = data.errors.map(e => e.detail || labels.somethingWentWrong);
            return { errorTitle, errorDetails, messages, status, instance, time };
        }
        else if (data?.warnings && data.warnings.length > 0)
        {
            const warnTitle = data.warnings[0].title ?? '';
            const firstWarning = data.warnings[0];
            const message = this.getMessage(firstWarning.responseId, firstWarning.details, firstWarning.message);
            return { errorTitle: warnTitle, errorDetails: errorDetails, messages: [message], status, instance, time };
        }
        else if (dataAsString)
        {
            return { errorTitle, errorDetails, messages: [dataAsString], status, instance, time };
        }
        else if (data)
        {
            const message = this.getMessage(data.responseId, data.details, data.message || data.detail);
            return { errorTitle, errorDetails, messages: [message], status, instance, time };
        }
        else
        {
            return { errorTitle, errorDetails, messages: [labels.somethingWentWrong], status, instance, time };
        }
    }

    private getTitle(responseId?: string, title?: string): string
    {
        if (responseId)
        {
            let localisedTitle = this.getApiMessage(responseId + "_Title");
            if (localisedTitle)
            {
                return localisedTitle;
            }
        }
        return title ?? '';
    }

    private static readonly argExpression = /\{\d+(:\w+)?\}/gi;
    private static readonly dateTimeExpression = /^(?:datetime')?(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d+Z)'?$/i;
    private static readonly badArg = "-";

    private getMessage(responseId?: string, details?: string[], message?: string): string
    {
        if (responseId)
        {
            let localisedMessage = this.getApiMessage(responseId + "_Message");
            if (localisedMessage)
            {
                let args = localisedMessage.match(ApiMessageBuilder.argExpression);
                if (args?.length ?? 0) // ensure there are enough details to honour the args
                {
                    let localisedMessageWithArgs = localisedMessage.replace(ApiMessageBuilder.argExpression, arg =>
                    {
                        const argMatch = arg.match(/\{(\d+)(:\w+)?\}/i) as RegExpMatchArray;
                        const argIndex = parseInt(argMatch[1] as string);
                        const detail = details?.[argIndex] ?? null;

                        if (detail == null)
                        {
                            return ApiMessageBuilder.badArg;
                        }

                        const format = argMatch[2]?.substring(1);
                        if (format == null)
                        {
                            return this.formatString(detail);
                        }

                        switch (format[0])
                        {
                            case 't':
                                return this.formatTime(detail);

                            case 'd':
                                return this.formatDate(detail);

                            case 'g':
                                return this.formatDateTime(detail);

                            case 'N':
                            case 'F':
                                return this.formatNumber(detail, format);

                            default:
                                return ApiMessageBuilder.badArg;
                        }
                    });

                    return localisedMessageWithArgs;
                }
            }
        }
        return (message ?? this.labels.somethingWentWrong);
    }

    private formatTime(value: string | number): string
    {
        if (typeof (value) != "string")
        {
            return ApiMessageBuilder.badArg;
        }

        const dateTimeMatch = value.match(ApiMessageBuilder.dateTimeExpression);
        const dateTimeString = dateTimeMatch?.[1];

        if (dateTimeString == null)
        {
            return ApiMessageBuilder.badArg;
        }

        const formattedValue = DateTime.fromISO(dateTimeString).toLocaleString(DateTime.TIME_SIMPLE, { locale: this.locale });
        return formattedValue;
    }

    private formatDate(value: string | number): string
    {
        if (typeof (value) != "string")
        {
            return ApiMessageBuilder.badArg;
        }

        const dateTimeMatch = value.match(ApiMessageBuilder.dateTimeExpression);
        const dateTimeString = dateTimeMatch?.[1];

        if (dateTimeString == null)
        {
            return ApiMessageBuilder.badArg;
        }

        const formattedValue = DateTime.fromISO(dateTimeString).toLocaleString(DateTime.DATE_SHORT, { locale: this.locale });
        return formattedValue;
    }

    private formatDateTime(value: string | number): string
    {
        if (typeof (value) != "string")
        {
            return ApiMessageBuilder.badArg;
        }

        const dateTimeMatch = value.match(ApiMessageBuilder.dateTimeExpression);
        const dateTimeString = dateTimeMatch?.[1];

        if (dateTimeString == null)
        {
            return ApiMessageBuilder.badArg;
        }

        const formattedValue = DateTime.fromISO(dateTimeString).toLocaleString(DateTime.DATETIME_SHORT, { locale: this.locale });
        return formattedValue;
    }

    private formatNumber(value: string | number, format: string): string
    {
        if (typeof (value) != "number")
        {
            return ApiMessageBuilder.badArg;
        }

        let decimalPlaces = parseInt(format.substring(1));
        if (isNaN(decimalPlaces))
        {
            decimalPlaces = 2;
        }

        const formattedValue = value.toFixed(decimalPlaces);
        return formattedValue;
    }

    private formatString(value: string | number): string
    {
        const formattedValue = (typeof (value) == "string" ? value : ApiMessageBuilder.badArg);
        return formattedValue;
    }
}

export interface IApiError
{
    errorTitle: string;
    errorDetails: string;
    messages: string[];
    status: number;
    instance: string;
    time: string;
}

export interface IApiErrorLabels
{
    networkFailure: string;
    pleaseTryAgain: string;
    somethingWentWrong: string;
}
